import React, { useEffect, useState } from 'react'; 
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext'; 
import { api } from '../utils/api'; 
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input'; 
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'; 
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'; 
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, UserPlus, FileDown, Upload, Edit, Trash2, Bell } from 'lucide-react'; 
import { toast } from 'sonner';

const Settings = () => { 
  const { user } = useAuth();
  const { loadSettings: reloadTheme } = useTheme(); 
  const [users, setUsers] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false); 
  const [notificationEmail, setNotificationEmail] = useState(''); 
  const [notificationPhone, setNotificationPhone] = useState(''); 
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'vendedor' });

  useEffect(() => { 
    if (user?.role === 'admin') { 
      fetchUsers(); 
      loadSettings(); 
    } 
  }, [user]);

  const loadSettings = async () => { 
    try { 
      const response = await api.settings.get(); 
      const settings = response.data;
      setNotificationEmail(settings.notification_email || ''); 
      setNotificationPhone(settings.notification_phone || ''); 
    } catch (error) { 
      console.error('Error loading settings:', error); 
    } 
  };

  const fetchUsers = async () => { 
    try { 
      const response = await api.users.getAll(); 
      setUsers(response.data);
    } catch (error) { 
      toast.error('Erro ao carregar usuários'); 
    } finally { 
      setLoading(false); 
    } 
  }; 

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    try { 
      if (editingUser) { 
        const updateData = { name: formData.name, email: formData.email, role: formData.role };
        if (formData.password) { updateData.password = formData.password; } 
        await api.users.update(editingUser.id, updateData); 
        toast.success('Usuário atualizado!'); 
      } else { 
        await api.users.create(formData); 
        toast.success('Usuário criado!');
      } 
      setDialogOpen(false); 
      resetForm(); 
      fetchUsers(); 
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário'); 
    } 
  };

  const handleDeleteUser = async (id) => { 
    if (id === user.id) { toast.error('Você não pode excluir a si mesmo.'); return; } 
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return; 
    try { 
      await api.users.delete(id); 
      toast.success('Usuário excluído!'); 
      fetchUsers();
    } catch (error) { 
      toast.error('Erro ao excluir usuário'); 
    } 
  }; 

  const openEditUserDialog = (userToEdit) => { 
    setEditingUser(userToEdit);
    setFormData({ name: userToEdit.name, email: userToEdit.email, password: '', role: userToEdit.role }); 
    setDialogOpen(true); 
  };

  const resetForm = () => { 
    setEditingUser(null); 
    setFormData({ name: '', email: '', password: '', role: 'vendedor' }); 
  };

  const handleSettingsUpdate = async () => { 
    try { 
      const updateData = { 
        notification_email: notificationEmail || null, 
        notification_phone: notificationPhone || null, 
      }; 
      await api.settings.update(updateData); 
      await reloadTheme(); 
      toast.success('Configurações atualizadas com sucesso!');
    } catch (error) { 
      toast.error('Erro ao atualizar configurações'); 
    } 
  }; 

  const handleDownloadTemplate = (type) => { 
    let content = '';
    let filename = ''; 
    if (type === 'products') { 
      content = 'name,price,stock,min_stock,category\nCerveja Lata,5.00,100,10,bebida'; 
      filename = 'modelo_produtos.csv';
    } else if (type === 'customers') { 
      content = 'name,phone,cpf_cnpj,birth_date\nJoão Silva,51999999999,12345678900,1990-01-01'; 
      filename = 'modelo_clientes.csv';
    } 
    const blob = new Blob([content], { type: 'text/csv' }); 
    const url = window.URL.createObjectURL(blob); 
    const a = document.createElement('a');
    a.href = url; 
    a.download = filename; 
    document.body.appendChild(a); 
    a.click(); 
    document.body.removeChild(a); 
  };

  if (user?.role !== 'admin') { 
    return ( 
      <div className="flex items-center justify-center h-96"> 
        <p className="text-muted-foreground">Acesso negado. Apenas administradores.</p> 
      </div> 
    );
  } 

  return ( 
    <div className="space-y-6" data-testid="settings-page"> 
      <h1 className="text-5xl font-bold uppercase tracking-tight">Configurações</h1> 
      <Tabs defaultValue="appearance" className="w-full"> 
        <TabsList className="bg-card border border-border"> 
          <TabsTrigger value="appearance" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"> 
            <Bell className="mr-2 h-4 w-4" /> Alertas 
          </TabsTrigger> 
          <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"> 
            <UserPlus className="mr-2 h-4 w-4" /> Usuários 
          </TabsTrigger> 
          <TabsTrigger value="import" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"> 
            <Upload className="mr-2 h-4 w-4" /> Importação 
          </TabsTrigger> 
        </TabsList> 

        <TabsContent value="appearance" className="space-y-6"> 
          <Card className="bg-card border-border" data-testid="notification-settings"> 
            <CardHeader> 
              <CardTitle className="text-xl uppercase tracking-wide">Notificações de Estoque Baixo</CardTitle> 
            </CardHeader> 
            <CardContent className="space-y-4"> 
              <div> 
                <Label className="text-xs uppercase tracking-wider">Email para Notificações</Label> 
                <Input type="email" value={notificationEmail} onChange={(e) => setNotificationEmail(e.target.value)} className="mt-2 font-mono" placeholder="seuemail@exemplo.com" data-testid="notification-email-input" /> 
              </div> 
              <div> 
                <Label className="text-xs uppercase tracking-wider">Telefone para SMS (Opcional)</Label> 
                <Input type="tel" value={notificationPhone} onChange={(e) => setNotificationPhone(e.target.value)} className="mt-2 font-mono" placeholder="(51) 99999-9999" data-testid="notification-phone-input" /> 
              </div> 
            </CardContent> 
          </Card> 

          <Button onClick={handleSettingsUpdate} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase h-12" data-testid="save-settings-button" > 
            Salvar Configurações 
          </Button> 
        </TabsContent> 

        <TabsContent value="users" className="space-y-6"> 
          <div className="flex items-center justify-between"> 
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}> 
              <DialogTrigger asChild> 
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase" data-testid="add-user-button"> 
                  <Plus className="mr-2 h-5 w-5" /> Novo Usuário 
                </Button> 
              </DialogTrigger> 
              <DialogContent className="bg-card border-border" data-testid="user-dialog"> 
                <DialogHeader> 
                  <DialogTitle className="text-2xl uppercase font-bold"> {editingUser ? 'Editar Usuário' : 'Novo Usuário'} </DialogTitle> 
                </DialogHeader> 
                <form onSubmit={handleSubmit} className="space-y-4"> 
                  <div> 
                    <Label className="text-xs uppercase tracking-wider">Nome</Label> 
                    <Input data-testid="user-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1 font-mono" required /> 
                  </div> 
                  <div> 
                    <Label className="text-xs uppercase tracking-wider">Email</Label> 
                    <Input data-testid="user-email-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1 font-mono" required /> 
                  </div> 
                  <div> 
                    <Label className="text-xs uppercase tracking-wider">Senha {editingUser && '(Deixe em branco para manter)'}</Label> 
                    <Input data-testid="user-password-input" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="mt-1 font-mono" required={!editingUser} /> 
                  </div> 
                  <div> 
                    <Label className="text-xs uppercase tracking-wider">Nível de Acesso</Label> 
                    <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}> 
                      <SelectTrigger className="mt-1 font-mono" data-testid="user-role-select"> 
                        <SelectValue /> 
                      </SelectTrigger> 
                      <SelectContent className="bg-card border-border"> 
                        <SelectItem value="admin">Administrador</SelectItem> 
                        <SelectItem value="vendedor">Vendedor</SelectItem> 
                        <SelectItem value="visualizador">Visualizador</SelectItem> 
                      </SelectContent> 
                    </Select> 
                  </div> 
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold uppercase" data-testid="user-submit-button"> 
                    {editingUser ? 'Atualizar' : 'Criar Usuário'} 
                  </Button> 
                </form> 
              </DialogContent> 
            </Dialog> 
          </div> 
          <Card className="bg-card border-border" data-testid="users-list"> 
            <CardHeader> 
              <CardTitle className="text-xl uppercase tracking-wide flex items-center gap-2"> 
                <UserPlus className="h-5 w-5 text-primary" /> Usuários do Sistema 
              </CardTitle> 
            </CardHeader> 
            <CardContent className="p-0"> 
              <Table> 
                <TableHeader> 
                  <TableRow className="border-border hover:bg-transparent"> 
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nome</TableHead> 
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Email</TableHead> 
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nível</TableHead> 
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Data de Criação</TableHead> 
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ações</TableHead> 
                  </TableRow> 
                </TableHeader> 
                <TableBody> 
                  {users.map((u) => ( 
                    <TableRow key={u.id} className="border-border/50 hover:bg-secondary/50" data-testid={`user-row-${u.id}`}> 
                      <TableCell className="font-medium">{u.name}</TableCell> 
                      <TableCell className="font-mono text-sm">{u.email}</TableCell> 
                      <TableCell> 
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${ u.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground' }`}> 
                          {u.role} 
                        </span> 
                      </TableCell> 
                      <TableCell className="font-mono text-sm"> {new Date(u.created_at).toLocaleDateString('pt-BR')} </TableCell> 
                      <TableCell className="text-right"> 
                        <div className="flex justify-end gap-2"> 
                          <Button variant="ghost" size="icon" onClick={() => openEditUserDialog(u)} className="hover:bg-primary/10 hover:text-primary" data-testid={`edit-user-${u.id}`} > 
                            <Edit className="h-4 w-4" /> 
                          </Button> 
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(u.id)} className="hover:bg-destructive/10 hover:text-destructive" data-testid={`delete-user-${u.id}`} disabled={u.id === user.id} > 
                            <Trash2 className="h-4 w-4" /> 
                          </Button> 
                        </div> 
                      </TableCell> 
                    </TableRow> 
                  ))} 
                </TableBody> 
              </Table> 
            </CardContent> 
          </Card> 
        </TabsContent> 

        <TabsContent value="import" className="space-y-6"> 
          <Card className="bg-card border-border"> 
            <CardHeader> 
              <CardTitle className="text-xl uppercase tracking-wide">Modelos de Importação</CardTitle> 
            </CardHeader> 
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4"> 
              <div className="p-4 border border-border rounded-md"> 
                <h3 className="font-bold mb-2">Modelo de Produtos</h3> 
                <p className="text-sm text-muted-foreground mb-4">CSV com colunas: name, price, stock, min_stock, category</p> 
                <Button variant="outline" className="w-full" onClick={() => handleDownloadTemplate('products')} > 
                  <FileDown className="mr-2 h-4 w-4" /> Download Modelo CSV 
                </Button> 
              </div> 
              <div className="p-4 border border-border rounded-md"> 
                <h3 className="font-bold mb-2">Modelo de Clientes</h3> 
                <p className="text-sm text-muted-foreground mb-4">CSV com colunas: name, phone, cpf_cnpj, birth_date</p> 
                <Button variant="outline" className="w-full" onClick={() => handleDownloadTemplate('customers')} > 
                  <FileDown className="mr-2 h-4 w-4" /> Download Modelo CSV 
                </Button> 
              </div> 
            </CardContent> 
          </Card> 
          <div className="text-sm text-muted-foreground"> 
            <p>Para importar os dados, vá até a página correspondente (Produtos ou Clientes) e clique no botão "Importar".</p> 
          </div> 
        </TabsContent> 
      </Tabs> 
    </div> 
  );
}; 

export default Settings;
